import React, {useContext, useState} from 'react';

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import {useAuth} from "../../../hooks/useAuth";
import {useNavigate} from "react-router-dom";
import * as Yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import {ToasterContext} from "../../shared/Toaster/ToasterContext";
import {NotificationType} from "../../../constants/global";
import './resetPassword.css';
import {routeConfigs} from "../../../navigation/routeConfig";
import {CircularProgress} from "@mui/material";

interface IResetPasswordProps {
  setIsConfirmed: (confirmed: boolean) => void,
  username: string;
}

const ResetPassword: React.FunctionComponent<IResetPasswordProps> = ({
  setIsConfirmed,
  username
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const {addToast} = useContext(ToasterContext);
  const auth: any = useAuth();
  const navigate  = useNavigate();

  const validationSchema = Yup.object().shape({
    code: Yup.string()
      .required('Code is Required'),
    password: Yup.string()
      .required('Password is required')
      .min(8, 'Password is too short - should be 8 chars minimum.')
      .matches(/[a-zA-Z]/, 'Password can only contain Latin letters.'),
    confirmPassword: Yup.string()
      .required('Password is required')
      .oneOf([Yup.ref('password')], 'Your passwords do not match.'),
  });

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const onSubmit = async (data: any) => {
    setLoading(true);
    const result = await auth.resetPassword(username, data.code, data.password);

    if (result.success) {
      setLoading(false);
      setIsConfirmed(false);
      addToast({ type: NotificationType.Success, title: 'Password has been successfully changed.'  });
      navigate ('/login');
    } else {
      setLoading(false);
      setIsConfirmed(true);
      addToast({ type: NotificationType.Error,  title: result.message });
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{mt: 2}}>
      <Box sx={{mt: 1}}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="code"
          label="Verify Code"
          autoComplete="code"
          autoFocus
          helperText={errors.code?.message?.toString()}
          {...register('code')}
          error={!!errors.code}
        />
        <TextField
          margin="normal"
          fullWidth
          id="password"
          label="New Password"
          autoComplete="password"
          type='password'
          helperText={errors.password?.message?.toString()}
          {...register('password')}
          error={!!errors.password}
        />
        <TextField
          margin="normal"
          fullWidth
          id="confirmPassword"
          label="Confirm Password"
          autoComplete="confirmPassword"
          type='password'
          helperText={errors.confirmPassword?.message?.toString()}
          {...register('confirmPassword')}
          error={!!errors.confirmPassword}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{mt: 3, mb: 2}}
        >
          Verify
          { loading && <CircularProgress sx={{ml: 1}} size={14} color='inherit' />}
        </Button>
        <Grid container>
          <Grid item>
            Already have an account?
            <Link href={routeConfigs.login.path} sx={{ml: 1}} variant="body2">
              Sign In
            </Link>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export default ResetPassword;